import React, { useMemo } from 'react';

import useValueFormatters from '../../../hooks/useValueFormatters';
import useGetFieldLabel from '../../../hooks/useGetFieldLabel';
import Icon from '../../../kingpin/atoms/Icon';
import getIdentifier from '../../../getIdentifier';

const useBonusEligibility = ({
  bonusProgram,
  rewardOutput,
}: {
  bonusProgram: BonusTypes.BonusProgram;
  rewardOutput: BonusTypes.Rewards.AnyDriverRewardsOutput;
}) => {
  const { formatField } = useValueFormatters();
  const { getFieldLabel } = useGetFieldLabel();
  const isQualified = rewardOutput.eligibleForBonus === 'qualified';

  const StatusIcon = useMemo(() => {
    if (!isQualified) {
      return (
        <Icon
          icon={'warning'}
          color={'var(--color-status-danger)'}
          width={18}
          height={18}
        />
      );
    }
    return (
      <Icon
        icon={'checkmark'}
        color={'var(--color-status-success)'}
        width={18}
        height={18}
      />
    );
  }, [isQualified]);

  const qualifiers = useMemo((): {
    key: string;
    label: string;
    isPassed: boolean;
    actual: string;
    target: string;
    output: BonusTypes.Rewards.QualifierOutput;
    fieldType: BonusRecordTagging.FieldType;
  }[] => {
    return rewardOutput.qualifiers.map((q) => {
      return {
        key: getIdentifier(undefined, true),
        label: getFieldLabel({
          field: q.name,
          dataType: bonusProgram.dataset,
        }),
        isPassed: q.passed,
        actual: formatField({
          value: q.actual || 0,
          field: q.name,
          dataset: bonusProgram.dataset,
        }).toString(),
        target: formatField({
          value: q.target,
          field: q.name,
          dataset: bonusProgram.dataset,
        }).toString(),
        output: q,
        fieldType: 'qualifier',
      };
    });
  }, [
    rewardOutput.qualifiers,
    getFieldLabel,
    bonusProgram.dataset,
    formatField,
  ]);

  const disqualifiers = useMemo((): {
    key: string;
    label: string;
    isPassed: boolean;
    actual: string;
    target: string;
    output: BonusTypes.Rewards.QualifierOutput;
    fieldType: BonusRecordTagging.FieldType;
  }[] => {
    return rewardOutput.disqualifiers.map((q) => {
      return {
        key: getIdentifier(undefined, true),
        label: getFieldLabel({
          field: q.name,
          dataType: bonusProgram.dataset,
        }),
        isPassed: !q.passed,
        actual: formatField({
          value: q.actual || 0,
          field: q.name,
          dataset: bonusProgram.dataset,
        }).toString(),
        target: formatField({
          value: q.target,
          field: q.name,
          dataset: bonusProgram.dataset,
        }).toString(),
        output: q,
        fieldType: 'disqualifier',
      };
    });
  }, [
    rewardOutput.disqualifiers,
    getFieldLabel,
    bonusProgram.dataset,
    formatField,
  ]);

  const expectations = useMemo(() => {
    return [...qualifiers, ...disqualifiers];
  }, [disqualifiers, qualifiers]);

  return {
    isQualified,
    expectations,
    StatusIcon,
  };
};

export default useBonusEligibility;
